import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const ContactSupportModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    email: '',
    problem: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData?.problem?.trim()) {
      newErrors.problem = 'Please explain your problem';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(false);
    setIsSubmitted(true);
  };

  const handleReturnToLogin = () => {
    setIsSubmitted(false);
    setFormData({ email: '', problem: '' });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg shadow-xl border border-border w-full max-w-md">
        {!isSubmitted ? (
          <>
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-heading font-semibold text-foreground">Contact Support</h2>
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground transition-smooth focus-ring rounded p-1"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <Input
                type="email"
                name="email"
                label="Email Address"
                placeholder="Enter your email address"
                value={formData?.email}
                onChange={handleInputChange}
                error={errors?.email}
                required
                disabled={isLoading}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Explain Problem <span className="text-destructive">*</span>
                </label>
                <textarea
                  name="problem"
                  value={formData?.problem}
                  onChange={handleInputChange}
                  placeholder="Describe your issue in detail..."
                  rows={5}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
                />
                {errors?.problem && (
                  <p className="text-xs text-destructive mt-1">{errors?.problem}</p>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isLoading}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  loading={isLoading}
                  disabled={isLoading}
                  fullWidth
                >
                  Submit
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="p-6 text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="CheckCircle" size={32} className="text-primary" />
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-heading font-semibold text-foreground">
                Request Submitted
              </h3>
              <p className="text-sm text-muted-foreground">
                Request submitted to the administrator
              </p>
            </div>
            <Button
              variant="default"
              onClick={handleReturnToLogin}
              fullWidth
            >
              Return to Login Screen
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactSupportModal;